/**
 * German user-facing text for the Bexio draft-create vocabulary.
 *
 * The BACKEND is the source of truth for every status and every blocker. This module
 * only TRANSLATES stable tokens into sentences a property manager can act on — it
 * never decides whether something is ready, and it never invents a token.
 *
 * An unknown token is shown verbatim rather than swallowed: a missing translation
 * must be visible, not silently rendered as "alles in Ordnung".
 */

/**
 * Row status in the drafts table. Derived ONLY from backend facts.
 *
 * NOT_CHECKED and UNKNOWN are deliberately separate, and conflating them was the
 * bug this vocabulary was split to fix. They answer different questions:
 *
 *   NOT_CHECKED  we have not asked the backend about this row yet.
 *   UNKNOWN      we asked, and the ledger came back with a status this frontend
 *                does not have a translation for.
 *
 * The first is a normal resting state and invites the user to press "prüfen". The
 * second is a gap between backend and frontend that somebody has to look at.
 * Labelling the second one "Noch nicht geprüft" would quietly swallow it — exactly
 * what this module's header forbids.
 *
 * CHECKING and CHECK_ERROR describe THIS screen's own request for a row, not a
 * backend state; they exist because a sweep over 30 candidates has to be legible
 * while it runs and has to survive a single row failing.
 */
export const ROW_STATUS = {
  NOT_CHECKED: "NOT_CHECKED",
  CHECKING: "CHECKING",
  READY: "READY",
  BLOCKED: "BLOCKED",
  CREATED: "CREATED",
  FAILED: "FAILED",
  // The provider deleted the Bexio document AFTER we created it. Deliberately its
  // own value: it is neither CREATED (nothing is there any more) nor FAILED (the
  // create succeeded), and folding it into either would tell the operator something
  // untrue about what happened.
  PROVIDER_MISSING: "PROVIDER_MISSING",
  CHECK_ERROR: "CHECK_ERROR",
  REVIEW: "REVIEW",
  UNKNOWN: "UNKNOWN",
};

export const ROW_STATUS_LABEL = {
  [ROW_STATUS.NOT_CHECKED]: "Noch nicht geprüft",
  [ROW_STATUS.CHECKING]: "Prüfung läuft …",
  [ROW_STATUS.READY]: "Bereit",
  [ROW_STATUS.BLOCKED]: "Blockiert",
  [ROW_STATUS.CREATED]: "Erstellt",
  // "Fehlgeschlagen" adopts the backend's own ledger wording
  // (bexio_draft_observability_service) and keeps "Prüfung fehlgeschlagen"
  // distinguishable from it — a create that failed is not a check that failed.
  [ROW_STATUS.FAILED]: "Fehlgeschlagen",
  [ROW_STATUS.PROVIDER_MISSING]: "Bexio-Entwurf nicht mehr vorhanden",
  [ROW_STATUS.CHECK_ERROR]: "Prüfung fehlgeschlagen",
  [ROW_STATUS.REVIEW]: "Manuelle Prüfung nötig",
  [ROW_STATUS.UNKNOWN]: "Unbekannter Status",
};

/**
 * Ledger status -> row status. Mirrors the backend ledger vocabulary
 * (pending | creating | created | failed | abandoned).
 *
 * `creating` is NOT "in Arbeit": the create path leaves that state behind when the
 * outcome is UNKNOWN, so it must read as something a human has to look at.
 */
export function rowStatusFromLedger(ledgerRow) {
  if (!ledgerRow) return null;
  switch (ledgerRow.status) {
    case "created":
      return ROW_STATUS.CREATED;
    case "provider_missing":
      return ROW_STATUS.PROVIDER_MISSING;
    case "failed":
      return ROW_STATUS.FAILED;
    case "creating":
    case "abandoned":
      return ROW_STATUS.REVIEW;
    case "pending":
      return ledgerRow.needs_attention ? ROW_STATUS.REVIEW : null;
    default:
      return ledgerRow.status ? ROW_STATUS.UNKNOWN : null;
  }
}

const BLOCKER_TEXT = {
  // ── connection / config ──
  blocked_no_active_connection:
    "Keine aktive Bexio-Verbindung. Bitte in den Bexio-Einstellungen verbinden.",
  blocked_before_cutover:
    "Dieser Mietmonat liegt vor dem konfigurierten Start der automatischen Entwürfe.",
  config_blocked: "Die Bexio-Rechnungskonfiguration ist unvollständig.",
  missing_bexio_connection: "Es ist keine Bexio-Verbindung hinterlegt.",
  missing_target_user_id: "Interner Ansprechpartner (Bexio-Benutzer) fehlt in der Konfiguration.",
  missing_default_language_id: "Standardsprache fehlt in der Konfiguration.",
  missing_template_slug: "Bexio-Vorlage fehlt in der Konfiguration.",
  missing_bank_account_id: "Bankkonto fehlt in der Konfiguration.",
  missing_currency_id: "Währung fehlt in der Konfiguration.",
  missing_payment_type_id: "Zahlungsart fehlt in der Konfiguration.",
  missing_mwst_type: "MWST-Typ fehlt in der Konfiguration.",
  missing_mwst_is_net: "MWST-Netto-Einstellung fehlt in der Konfiguration.",
  missing_show_position_taxes: "Anzeige der Positionssteuern fehlt in der Konfiguration.",
  missing_default_tax_id: "Standard-Steuersatz fehlt in der Konfiguration.",
  missing_draft_create_start_period: "Startperiode für Entwürfe fehlt in der Konfiguration.",

  // ── context / business ──
  context_blocked: "Der Rechnungsinhalt konnte nicht vollständig ermittelt werden.",
  no_positive_invoice_positions:
    "Für diesen Monat ergibt sich kein positiver Rechnungsbetrag.",

  // ── contact ──
  contact_missing_mapping: "Diesem Mietverhältnis ist kein Bexio-Kontakt zugeordnet.",
  contact_ambiguous_mapping:
    "Mehrere Bexio-Kontakte sind zugeordnet — es muss genau einer sein.",
  contact_invalid_id: "Die hinterlegte Bexio-Kontakt-ID ist ungültig.",
  contact_not_in_mirror:
    "Der zugeordnete Bexio-Kontakt ist im Vantio-Spiegel nicht auffindbar. Bitte synchronisieren.",

  // ── payload validation ──
  payload_missing_title: "Der Rechnungstitel konnte nicht gebildet werden.",
  payload_invalid_footer: "Die Fusszeile ist ungültig.",
  payload_api_reference_too_long:
    "Die interne Referenz überschreitet die von Bexio erlaubte Länge.",
  payload_invalid_api_reference: "Die interne Referenz ist ungültig.",
  payload_total_mismatch:
    "Positionssumme und Gesamtbetrag stimmen nicht überein — bitte die Umsatzzeilen prüfen.",
  payload_missing_total: "Der Gesamtbetrag fehlt.",
  payload_no_positions: "Es gibt keine Rechnungspositionen.",
  payload_unrounded_position_amount:
    "Ein Betrag hat mehr als zwei Nachkommastellen.",
  payload_invalid_position_amount: "Ein Positionsbetrag ist ungültig.",
  payload_missing_position_account_id: "Einer Position fehlt das Erlöskonto.",
  payload_missing_position_tax_id: "Einer Position fehlt der Steuersatz.",
  payload_missing_position_description: "Einer Position fehlt der Text.",
  payload_invalid_contact_id: "Die Bexio-Kontakt-ID ist ungültig.",

  // ── ledger state ──
  existing_creating_manual_review:
    "Ein früherer Versuch ist mit unklarem Ausgang stehengeblieben. Bitte in Bexio prüfen, ob bereits eine Rechnung existiert.",
  existing_failed_retry_not_allowed:
    "Ein früherer Versuch ist fehlgeschlagen. Ein erneuter Versuch muss ausdrücklich freigegeben werden.",
  existing_pending_dirty_manual_review:
    "Der Vorgang trägt Spuren eines früheren Versuchs und muss geprüft werden.",
  existing_abandoned_manual_review: "Der Vorgang wurde aufgegeben und ist gesperrt.",
  existing_unknown_status_manual_review: "Der gespeicherte Status ist unbekannt.",

  // ── recovery ──
  recovery_search_failed:
    "Die Prüfung auf eine bereits vorhandene Rechnung war nicht möglich. Es wurde nichts erstellt.",
  recovery_multiple_matches: "Mehrere Bexio-Rechnungen tragen dieselbe Referenz.",
  recovery_match_status_not_accepted:
    "Eine passende Bexio-Rechnung existiert, ist aber kein Entwurf mehr.",
  recovery_match_api_reference_mismatch: "Die gefundene Rechnung gehört nicht zu diesem Vorgang.",

  // ── create outcome ──
  bexio_auth_401: "Bexio hat die Anmeldung abgelehnt (401).",
  bexio_auth_403: "Bexio hat den Zugriff verweigert (403).",
  bexio_create_indeterminate:
    "Der Ausgang ist unklar — es ist nicht gesichert, ob Bexio eine Rechnung erstellt hat. Bitte in Bexio prüfen.",
  bexio_rejected_unknown: "Bexio hat die Erstellung abgelehnt.",
  tenancy_not_found: "Mietverhältnis nicht gefunden.",
  bexio_client_configuration_unavailable:
    "Die Bexio-Verbindung ist derzeit nicht nutzbar.",
};

/** Prefix tokens that carry a `:<revenue_type>` suffix. */
const PREFIXED = [
  {
    prefix: "ambiguous_service_period:",
    text: (t) =>
      `Mehrere Umsatzzeilen vom Typ „${t}" ergeben unterschiedliche Zeiträume — es gibt keinen eindeutigen Leistungszeitraum.`,
  },
  {
    prefix: "missing_account_map:",
    text: (t) => `Für den Umsatztyp „${t}" ist kein Bexio-Erlöskonto hinterlegt.`,
  },
  { prefix: "negative_amount:", text: (t) => `Der Betrag für „${t}" ist negativ.` },
  {
    prefix: "payload_missing_config_field:",
    text: (t) => `Konfigurationsfeld „${t}" fehlt.`,
  },
  {
    prefix: "bexio_rejected_",
    text: (code) => `Bexio hat die Erstellung abgelehnt (HTTP ${code}).`,
  },
];

/**
 * Translate one backend token into German.
 * Unknown tokens are returned verbatim so a gap is visible rather than hidden.
 */
export function blockerText(token) {
  if (!token) return "";
  if (BLOCKER_TEXT[token]) return BLOCKER_TEXT[token];
  for (const { prefix, text } of PREFIXED) {
    if (token.startsWith(prefix)) return text(token.slice(prefix.length));
  }
  return token;
}

/** Labels for the technical checklist in the preview panel. */
export const CHECK_LABEL = {
  api_reference_valid: "Interne Referenz gültig",
  contact_resolved: "Bexio-Kontakt aufgelöst",
  config_complete: "Konfiguration vollständig",
  account_map_complete: "Erlöskonten zugeordnet",
  tax_config_complete: "Steuersatz gesetzt",
};

/**
 * Three-way state of one technical check, read from the backend's own answer.
 *
 * `checks_not_evaluated` is authoritative and is NOT re-derived here: which check a
 * given pipeline exit reaches depends on the backend's step order, and a copy of
 * that order living in the browser would go silently wrong the first time a step
 * moves. This function only reads the list the backend already sends.
 */
export function checkState(preflight, key) {
  const skipped = preflight?.checks_not_evaluated;
  if (Array.isArray(skipped) && skipped.includes(key)) return "not_evaluated";
  return preflight?.technical_checks?.[key] === true ? "passed" : "failed";
}

/** CHF with Swiss thousands separator. */
export function formatChf(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `CHF ${Number(value).toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

export function monthLabel(year, month) {
  return `${MONTHS[month - 1] ?? month} ${year}`;
}

/** ISO date -> DD.MM.YYYY (the form the tenant sees on the invoice). */
export function formatSwissDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return String(iso);
  return `${d}.${m}.${y}`;
}
